import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Contact, ListChecks, GraduationCap, TriangleAlert, ArrowRight, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { StatusBadge } from "@/pages/portal/Leads";

function KPI({ icon: Icon, label, value, sub, testId }) {
  return (
    <Card className="rounded-[16px] card-hover" data-testid={testId}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
          <Icon className="h-4 w-4 text-[#BFA76A]" />
        </div>
        <p className="mt-2 font-mono-num text-3xl font-semibold">{value}</p>
        {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const { t, tt } = useLang();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/portal/dashboard").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 rounded-[16px]" />)}
        </div>
      </div>
    );
  }

  const k = data?.kpis || {};
  const isTrainee = user?.role === "trainee";

  return (
    <div className="space-y-6" data-testid="portal-dashboard">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">{t("Eleven Mitarbeiterportal", "Eleven Employee Portal")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("Ihre \u00dcbersicht als", "Your overview as")} {tt(ROLE_LABELS[user?.role])}
          {data?.onboarding_status === "onboarding" && (
            <Badge variant="outline" className="ml-2 rounded-full text-[10px] border-[#D6C08D]/50 text-[#A8905A]">Onboarding</Badge>
          )}
        </p>
      </div>

      {k.mandatory_open > 0 && (
        <Card className="rounded-[16px] border-[#D6C08D]/40 bg-[#D6C08D]/8" data-testid="mandatory-warning">
          <CardContent className="p-4 flex items-center gap-3">
            <TriangleAlert className="h-5 w-5 text-[#A8905A] shrink-0" />
            <p className="text-sm flex-1">
              {t(
                `Sie haben ${k.mandatory_open} offene Pflichtschulung(en) in der Eleven Academy.`,
                `You have ${k.mandatory_open} open mandatory training(s) in the Eleven Academy.`
              )}
            </p>
            <Link to="/academy" className="text-sm font-medium inline-flex items-center gap-1 hover:gap-2 transition-all">
              {t("Zur Academy", "To Academy")} <ArrowRight className="h-4 w-4" />
            </Link>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {!isTrainee && <KPI icon={Contact} label="Leads" value={k.my_leads ?? 0} sub={`+${k.new_leads_week ?? 0} ${t("diese Woche", "this week")}`} testId="kpi-leads" />}
        <KPI icon={ListChecks} label={t("Offene Aufgaben", "Open tasks")} value={k.open_tasks ?? 0} testId="kpi-tasks" />
        <KPI icon={GraduationCap} label={t("Schulungen", "Trainings")} value={`${k.courses_completed ?? 0}/${k.courses_total ?? 0}`} sub={`${k.training_pct ?? 0}% ${t("abgeschlossen", "completed")}`} testId="kpi-training" />
        <KPI icon={TrendingUp} label={t("Pflichtschulungen offen", "Mandatory open")} value={k.mandatory_open ?? 0} testId="kpi-mandatory" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {!isTrainee && (
          <Card className="rounded-[16px]">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base font-semibold">{t("Neueste Leads", "Latest leads")}</CardTitle>
              <Link to="/portal/leads" className="text-xs font-medium text-muted-foreground hover:text-foreground">{t("Alle anzeigen", "View all")}</Link>
            </CardHeader>
            <CardContent className="space-y-1">
              {(data?.recent_leads || []).length === 0 && (
                <p className="text-sm text-muted-foreground py-4">{t("Keine Leads vorhanden.", "No leads available.")}</p>
              )}
              {(data?.recent_leads || []).map((l) => (
                <Link key={l.id} to="/portal/leads" className="flex items-center justify-between rounded-[10px] px-3 py-2.5 hover:bg-muted transition-colors">
                  <div>
                    <p className="text-sm font-medium">{l.first_name} {l.last_name}</p>
                    <p className="text-xs text-muted-foreground">{l.pipeline}</p>
                  </div>
                  <StatusBadge status={l.status} />
                </Link>
              ))}
            </CardContent>
          </Card>
        )}
        <Card className="rounded-[16px]">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold">{t("Meine Aufgaben", "My tasks")}</CardTitle>
            <Link to="/portal/aufgaben" className="text-xs font-medium text-muted-foreground hover:text-foreground">{t("Alle anzeigen", "View all")}</Link>
          </CardHeader>
          <CardContent className="space-y-1">
            {(data?.recent_tasks || []).length === 0 && (
              <p className="text-sm text-muted-foreground py-4">{t("Keine Aufgaben vorhanden.", "No tasks available.")}</p>
            )}
            {(data?.recent_tasks || []).map((task) => (
              <div key={task.id} className="flex items-center justify-between rounded-[10px] px-3 py-2.5 hover:bg-muted transition-colors">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{task.title}</p>
                  <p className="text-xs text-muted-foreground">{task.due_date ? new Date(task.due_date).toLocaleDateString() : "\u2014"}</p>
                </div>
                <Badge variant={task.status === "done" ? "secondary" : "outline"} className="rounded-full text-[10px] shrink-0">
                  {task.status === "open" ? t("Offen", "Open") : task.status === "doing" ? t("In Arbeit", "Doing") : t("Erledigt", "Done")}
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>
        {isTrainee && (
          <Card className="rounded-[16px]">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold">{t("Ihr Schulungsfortschritt", "Your training progress")}</CardTitle>
            </CardHeader>
            <CardContent>
              <Progress value={k.training_pct || 0} className="h-2" />
              <p className="mt-3 text-sm text-muted-foreground">
                {t("Schlie\u00dfen Sie alle Pflichtschulungen ab, um vollen Zugriff zu erhalten.", "Complete all mandatory trainings to gain full access.")}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {data?.team && (
        <Card className="rounded-[16px]" data-testid="team-overview">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold">{t("Team\u00fcbersicht", "Team overview")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {data.team.map((m) => (
                <div key={m.id} className="rounded-[12px] border border-border p-4 flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-[#0B1220] text-[#D6C08D] flex items-center justify-center text-xs font-semibold shrink-0">
                    {m.first_name[0]}{m.last_name[0]}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{m.first_name} {m.last_name}</p>
                    <p className="text-xs text-muted-foreground">{tt(ROLE_LABELS[m.role])}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-mono-num text-sm font-semibold">{m.leads_count}</p>
                    <p className="text-[10px] text-muted-foreground">Leads</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
